import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { DateFormat, SemboDate } from '@libs/sembo-dates';
import type {
  ContentApiImage,
  DestinationStaticInfo,
  OverviewDestinationStaticInfo,
} from '@libs/trip-planner-interfaces';

import type {
  MultiIngredientAccommodationCard,
  PeekPriceSmallHotelCardData,
} from '../models';
import { PeekPriceModalCardType } from '../models';

const { t } = i18n.global;

export function getAccommodationDatesText(
  displayDate: Date | undefined,
  totalNights: number
): string | undefined {
  if (!displayDate) {
    return;
  }

  const startDate = new SemboDate(displayDate);
  const endDate = new SemboDate(displayDate).add({
    days: totalNights,
  });
  const sameMonth =
    startDate.format(DateFormat.MonthFullName) ===
    endDate.format(DateFormat.MonthFullName);

  return sameMonth
    ? `${startDate.format(DateFormat.Day)} - ${endDate.format(
        DateFormat.Day
      )} ${endDate.format(DateFormat.FullMonthAndYear)}`
    : `${startDate.format(DateFormat.Day)} ${startDate.format(
        DateFormat.MonthFullName
      )} - ${endDate.format(DateFormat.Day)} ${endDate.format(
        DateFormat.FullMonthAndYear
      )}`;
}

export function getAccommodationSubtitleText(
  product: DestinationStaticInfo | OverviewDestinationStaticInfo
): string {
  if (product.mainPolygon) {
    return product.mainPolygon.fullName;
  }

  if (product?.address?.city) {
    return product.address.country
      ? `${product.address.city}, ${product.address.country}`
      : product.address.city;
  }

  return '';
}

export function getAccommodationNightsText(
  numberOfNights: number | undefined
): string {
  return numberOfNights
    ? `${numberOfNights} ${t('planner.nights', numberOfNights)}`
    : '';
}

interface GetMultiIngredientAccommodationCardLoadingDataParams {
  date: Date | undefined;
  numberOfNights: number;
  hotelStaticInfo:
    OverviewDestinationStaticInfo | DestinationStaticInfo | undefined;
  getImageUrl: (file: ContentApiImage) => string;
}

export function getMultiIngredientAccommodationCardLoadingData(
  params: GetMultiIngredientAccommodationCardLoadingDataParams
): MultiIngredientAccommodationCard {
  const nights = getAccommodationNightsText(params.numberOfNights);
  const dates = getAccommodationDatesText(params.date, params.numberOfNights);
  const data: PeekPriceSmallHotelCardData = params.hotelStaticInfo
    ? {
        dates,
        imageUrl: getAccommodationImageUrls(
          params.hotelStaticInfo.images,
          1,
          params.getImageUrl
        )[0],
        nights,
        hotelName: params.hotelStaticInfo.name,
        hotelDestination: getAccommodationSubtitleText(params.hotelStaticInfo),
        rooms: undefined,
      }
    : {
        dates,
        imageUrl: undefined,
        nights,
        hotelName: undefined,
        hotelDestination: undefined,
        rooms: undefined,
      };

  return {
    type: PeekPriceModalCardType.Accommodation,
    data,
  };
}

export function getAccommodationImageUrls(
  rawLinks: ContentApiImage[],
  imagesLimit: number,
  getImageUrl: (image: ContentApiImage) => string
): string[] {
  return rawLinks
    .slice(0, imagesLimit)
    .map((image: ContentApiImage) => getImageUrl(image));
}
